import { createSlice } from "@reduxjs/toolkit"
import { api } from "./helpers/api"
import { LOADING_INITIAL_STATE, setFailed, setReceived, setRequested } from "modules/loader-watchers/helpers/loading"
import { getTimezone } from "pages/guide/dashboard/DashboardPageV2/utils"


const BASE = (process.env.REACT_APP_DASHBOARD_API_URL )

const url = (path) => `${BASE}${path}`


// ── Slice ─────────────────────────────────────────────────────────────────────

const aiDashboardSlice = createSlice({
  name: "aiDashboard",
  initialState: {
    data: { ...LOADING_INITIAL_STATE },
    kpi: { ...LOADING_INITIAL_STATE },
  },
  reducers: {
    dataRequested: (state) => { setRequested(state.data) },
    dataReceived: (state) => { setReceived(state.data) },
    dataFailed: (state) => { setFailed(state.data) },
    kpiRequested: (state) => { setRequested(state.kpi) },
    kpiReceived: (state) => { setReceived(state.kpi) },
    kpiFailed: (state) => { setFailed(state.kpi) },
  }
})

export const { dataRequested, dataReceived, dataFailed, kpiRequested, kpiReceived, kpiFailed } = aiDashboardSlice.actions
export default aiDashboardSlice.reducer

// ── API helpers ───────────────────────────────────────────────────────────────

export const getAIDashboardKpiData = (kpi, params) =>
  api({ url: url("/kpi-data"), params: { kpi, ...params }, suppressErrorNotification: true })

export const getAIDashboardWidgets = (params) =>
  api({ url: url("/widgets"), params, suppressErrorNotification: true })

export const getAIDashboardWidgetData = (widgetId, params) =>
  api({ url: url(`/widgets/${widgetId}/data`), params, suppressErrorNotification: true })

export const getAIDashboardWidgetCatalog = () =>
  api({ url: url("/widgets/catalog"), suppressErrorNotification: true })

export const addAIDashboardWidget = (data) =>
  api({ url: url("/widgets"), method: "post", data, suppressErrorNotification: true })

export const updateAIDashboardWidget = (widgetId, data) =>
  api({ url: url(`/widgets/${widgetId}`), method: "put", data, suppressErrorNotification: true })

export const deleteAIDashboardWidget = (widgetId) =>
  api({ url: url(`/widgets/${widgetId}`), method: "delete", suppressErrorNotification: true })

export const reorderAIDashboardWidgets = (positions) =>
  api({ url: url("/widgets/reorder"), method: "put", data: { positions }, suppressErrorNotification: true })

export const getAIDashboardState = () =>
  api({ url: url("/dashboard/state"), suppressErrorNotification: true })

export const saveAIDashboardState = (data) =>
  api({ url: url("/dashboard/state"), method: "put", data, suppressErrorNotification: true })

export const resetAIDashboardWidgets = () =>
  api({ url: url("/widgets/reset"), method: "post", suppressErrorNotification: true })

export const saveAIDashboardChartFromChat = (data) =>
  api({ url: url("/widgets/save-from-chat"), method: "post", data, suppressErrorNotification: true })

const aiDashboardChatRequest = (message, sessionId) =>
  api({ url: url("/chat"), method: "post", data: { message, session_id: sessionId, timezone: getTimezone() }, suppressErrorNotification: true, refresh: false })

export const aiDashboardChat = (message, sessionId) => async (dispatch) => {
  try {
    return await dispatch(aiDashboardChatRequest(message, sessionId))
  } catch (error) {
    if (error?.status !== 401) throw error
    const refreshToken = localStorage.getItem("assume_refresh_token") || localStorage.getItem("refresh_token")
    if (!refreshToken) throw error
    const prefix = localStorage.getItem("assume_refresh_token") ? "assume_" : ""
    const { default: axios } = await import("axios")
    const API = (await import("constants/api")).default
    const res = await axios.post(API.REFRESH_TOKEN_URL, { refresh_token: refreshToken, grant_type: "refresh_token" })
    localStorage.setItem(`${prefix}access_token`, res.data.access_token)
    localStorage.setItem(`${prefix}refresh_token`, res.data.refresh_token)
    return await dispatch(aiDashboardChatRequest(message, sessionId))
  }
}

export const getAIDashboardChatHistory = (sessionId) =>
  api({ url: url("/chat/history"), params: { ...(sessionId ? { session_id: sessionId } : {}), limit: 200 }, suppressErrorNotification: true })

export const getChatSessions = (limit = 10, offset = 0) =>
  api({ url: url("/chat/sessions"), params: { limit, offset }, suppressErrorNotification: true })

export const getChatHistory = (sessionId, limit = 50, offset = 0) =>
  api({ url: url("/chat/history"), params: { session_id: sessionId, limit, offset }, suppressErrorNotification: true })

export const renameChatSession = (sessionId, title) =>
  api({ url: url(`/chat/sessions/${sessionId}`), method: "patch", data: { title }, suppressErrorNotification: true })

export const deleteChatSession = (sessionId) =>
  api({ url: url(`/chat/sessions/${sessionId}`), method: "delete", suppressErrorNotification: true })
